import { GuestSiteUnlockStateDocument } from '@apps/guest/shared/hooks/GuestSiteUnlockState.graphql.generated';
import { useEventNameMatch, useEventUserRole } from '@shared/components/AuthProvider/AuthProvider.utils';
import { useTranslation } from '@shared/core';
import { useGetEventCTAButtonsDataQuery } from './CtaButtons.graphql.generated';
import { useLogoutEventSessionMutation } from '@graphql/queries/eventSession.graphql.generated';
import { buildRsvpPath, performFullPageRedirect } from '@shared/utils/navigation';
import { useCallback } from 'react';
import type { GraphQLError } from 'graphql';
import type { CtaButtonsSessionData } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.types';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { useRsvpDeadlineGuard } from '@apps/guest/packages/layout-engine/common/utils/useRsvpDeadlineGuard';
import { isRsvpEffectivelyEnabled } from '@apps/guest/packages/layout-engine/common/utils/legacyRsvpQuestions';
import useQueryString from '@shared/utils/hooks/useQueryString';
import { addError } from '@shared/utils/logger';
import { useDraftPreviewVisibility } from '@apps/guest/shared/hooks/useDraftPreviewVisibility';
import { useMcpPreviewQueryOptions } from '@apps/guest/shared/hooks/useMcpPreviewQueryOptions';

interface UseCtaButtonsControllerProps
  extends Readonly<{
    onRSVPButtonClicked?: () => void;
    onNotYouButtonClicked?: () => void;
    onNotYouErrorRequest?: (err: GraphQLError) => void;
    customRsvpButtonText?: string;
    customUnlockButtonText?: string;
  }> {}

export const useCtaButtonsController = (props: UseCtaButtonsControllerProps) => {
  const { onRSVPButtonClicked, customRsvpButtonText, customUnlockButtonText } = props;
  const { eventName } = useEventNameMatch();
  const { getButtonsVisibility, getUnlockButtonText, getRSVPButtonText, rsvpButtonCustomLabel } = useCtaButtons();
  const queryString = useQueryString();
  const { isRsvpClosedByDeadline, showDeadlineToGuest, deadlineDate, deadlineNote, deadlineTimezone } = useRsvpDeadlineGuard();

  const [logoutEvent] = useLogoutEventSessionMutation();

  // Marking this query as `ssr: false` because any RSVP data query is proxied through app-server-api -- which takes long to resolve.
  // MCP preview resolves it server-side instead — see MCP_PREVIEW.md#cache-first-reads.
  const { forceSsr: isMcpPreview, ssr: mcpPreviewSsr, fetchPolicy: mcpPreviewFetchPolicy } = useMcpPreviewQueryOptions();
  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventName || '' },
    batchMode: 'fast',
    ssr: mcpPreviewSsr,
    fetchPolicy: mcpPreviewFetchPolicy,
  });

  const { isDraftViewAuthorized } = useDraftPreviewVisibility(eventName);

  const { isAdmin } = useEventUserRole();

  const event: CtaButtonsSessionData = sessionData?.eventByName ?? {};

  const {
    activeSession,
    settings,
    welcomePage,
    storyPage,
    schedulePage,
    travelPage,
    faqPage,
    weddingPage,
    registryPage,
    momentsPage,
    rsvpPage,
    customPages,
    hasEnabledLegacyRsvpQuestions,
  } = event;
  const { hasIdentity, eventCTA, hasUnlocked, person } = activeSession || {};
  const { label: rsvpLabel, rsvpIsEnabled: rawRsvpIsEnabled, rsvpAttendanceStatus, scheduleHasInviteOnlyItems, hasSpecificGuestsPages } = eventCTA || {};
  // Any specificGuests page the guest hasn't revealed yet — drives find-invite regardless of a password gate.
  const hasUnrevealedSpecificGuestPages = Boolean(hasSpecificGuestsPages) && !hasIdentity;
  const rsvpIsEnabled = isRsvpEffectivelyEnabled(rawRsvpIsEnabled, hasEnabledLegacyRsvpQuestions);
  const guestName = person?.fullName ?? '';
  const { tq } = useTranslation('layoutEngine');
  const buttonNotYouText = tq`Not You?`;
  const personalizedText = tq`Hello, ${guestName}.`;
  const rsvpButtonText = rsvpButtonCustomLabel || customRsvpButtonText || getRSVPButtonText(rsvpAttendanceStatus, rsvpLabel);

  const pages = [welcomePage, storyPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, ...(customPages ?? [])];

  const rsvpIsPasswordProtected = rsvpPage?.private;
  // isSpecificGuestsOnlyUnlock is true only when the specificGuests swap surfaced the unlock button
  // (no password gate); password/blocked-pages unlocks return it false so they keep the password form.
  const {
    shouldDisplayUnlockButton: shouldDisplayUnlockButtonBase,
    shouldDisplayRSVPButton,
    isSpecificGuestsOnlyUnlock,
  } = getButtonsVisibility({
    pages,
    welcomePage,
    schedulePage,
    rsvpIsEnabled,
    rsvpIsPasswordProtected,
    hasUnlocked,
    hasIdentity,
    hasSpecificGuestsPages,
    requirePasswordForEventDate: settings?.requirePasswordForEventDate,
    requirePasswordForEventLocation: settings?.requirePasswordForEventLocation,
    customPages,
  });
  const shouldDisplayUnlockButton = isDraftViewAuthorized ? false : shouldDisplayUnlockButtonBase;

  // The specificGuests swap is an identify action, not a schedule unlock — never label it "Unlock Your
  // Schedule" (that's schedule-page only), even when the guest has already unlocked but isn't identified.
  const unlockButtonText = getUnlockButtonText(hasUnlocked && !isSpecificGuestsOnlyUnlock, customUnlockButtonText);

  // MCP preview: suppress the RSVP CTA — see MCP_PREVIEW.md#charm-flow-suppression.
  const finalShouldDisplayRSVPButton = shouldDisplayRSVPButton && !isRsvpClosedByDeadline && !isMcpPreview;

  // Show the custom post-deadline note only where the RSVP button itself would
  // have appeared for this guest. `shouldDisplayRSVPButton` here is the pre-deadline
  // visibility, so a guest with RSVP disabled/hidden or still locked behind a
  // password (who never saw the button) won't see the note that replaces it.
  const showDeadlineNote = shouldDisplayRSVPButton && isRsvpClosedByDeadline && !!deadlineNote;

  const rsvpHandle = useCallback(() => {
    onRSVPButtonClicked?.();
    performFullPageRedirect(buildRsvpPath(eventName, queryString));
  }, [onRSVPButtonClicked, eventName, queryString]);

  const logoutHandle = async () => {
    await logoutEvent({ refetchQueries: () => ['GetEventSession', 'GetGuestSiteSchedule', 'GetEventCTAButtonsData', GuestSiteUnlockStateDocument] })
      .then(() => props.onNotYouButtonClicked?.())
      .catch(err => {
        addError(err, { context: 'CtaButtons', operation: 'logoutEvent' });
        props.onNotYouErrorRequest?.(err);
      });
  };

  return {
    isAdmin,
    guestName,
    personalizedText,
    buttonNotYouText,
    rsvpButtonText,
    shouldDisplayRSVPButton: finalShouldDisplayRSVPButton,
    rsvpHandle,
    scheduleHasInviteOnlyItems,
    hasIdentity,
    logoutHandle,
    shouldDisplayUnlockButton,
    isSpecificGuestsOnlyUnlock,
    hasUnrevealedSpecificGuestPages,
    unlockButtonText,
    showDeadlineToGuest,
    deadlineDate,
    deadlineTimezone,
    showDeadlineNote,
    deadlineNote,
  };
};
